import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { SiGoogle, SiApple } from 'react-icons/si';
import { FaUserNinja } from 'react-icons/fa6';
import ga from '../services/analytics';
import CassetteLoader from '../components/CassetteLoader';
import { preserveUtmParams } from '../utils/utm';
import { getPostAuthPath } from '../utils/onboardingNavigation';
import { authStore, resolveIsAnonymous, type AuthSnapshot } from '../auth/authStore';
import { SketchPageShell, SketchBox, SketchButton } from '../components/sketchy';

const SETTLE_TIMEOUT_MS = 8000;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    signInWithGoogle,
    signInWithApple,
    signInAnonymously,
    linkWithGoogle,
    linkWithApple,
    loading,
    isAnonymous,
  } = useAuth();
  const [authInProgress, setAuthInProgress] = useState(false);

  useEffect(() => {
    ga.pageView('/auth/login', 'Login - Cassette');
  }, []);

  useEffect(() => {
    return () => {
      setAuthInProgress(false);
    };
  }, []);

  const navigateForSnapshot = (snap: AuthSnapshot) => {
    const path = getPostAuthPath(
      snap.isNewUser,
      snap.driveLinked || snap.storageProvider === 'cassette_s3',
      resolveIsAnonymous(snap),
    );
    navigate(preserveUtmParams(path), { replace: true });
  };

  // After a fresh popup sign-in, wait for the auth store to settle, then route.
  const routeAfterAuth = async () => {
    const settled = await Promise.race([
      authStore.waitForSettled(),
      new Promise<null>((resolve) => window.setTimeout(() => resolve(null), SETTLE_TIMEOUT_MS)),
    ]);
    if (settled && settled.user) {
      navigateForSnapshot(settled);
    } else {
      setAuthInProgress(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthInProgress(true);
    try {
      if (isAnonymous) {
        await linkWithGoogle();
        const snap = authStore.getSnapshot();
        if (snap.user && !snap.user.isAnonymous && !snap.isAnonymous) {
          navigateForSnapshot(snap);
        } else {
          setAuthInProgress(false);
        }
      } else {
        signInWithGoogle();
        await routeAfterAuth();
      }
    } catch {
      setAuthInProgress(false);
    }
  };

  const handleAppleSignIn = async () => {
    setAuthInProgress(true);
    try {
      if (isAnonymous) {
        await linkWithApple();
        const snap = authStore.getSnapshot();
        if (snap.user && !snap.user.isAnonymous && !snap.isAnonymous) {
          navigateForSnapshot(snap);
        } else {
          setAuthInProgress(false);
        }
      } else {
        signInWithApple();
        await routeAfterAuth();
      }
    } catch {
      setAuthInProgress(false);
    }
  };

  const handleAnonymous = async () => {
    setAuthInProgress(true);
    try {
      await signInAnonymously();
      await routeAfterAuth();
    } catch {
      setAuthInProgress(false);
    }
  };

  if (loading || authInProgress) {
    return (
      <SketchPageShell className="min-h-screen flex flex-col items-center justify-center px-4">
        <CassetteLoader size={80} variant="overlay" text={t('login.checkingAuth')} />
      </SketchPageShell>
    );
  }

  return (
    <SketchPageShell className="min-h-screen flex flex-col items-center justify-center px-4">
      <SketchBox className="p-6 max-w-md w-full">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[var(--color-text)] mb-2 scribble-underline inline-block">
            {isAnonymous ? t('auth.guest.linkTitle', 'Connect account to save progress') : t('login.welcome')}
          </h2>
          <p className="text-[var(--color-muted)] mb-4">
            {isAnonymous
              ? t('auth.guest.linkSubtitle', 'Link Google or Apple to keep your guest session.')
              : t('login.subtitle')}
          </p>

          <div className="space-y-3">
            <SketchButton
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 text-base"
            >
              <SiGoogle className="w-6 h-6" style={{ color: 'var(--brand-from)' }} />
              {t('auth.continueWithGoogle')}
            </SketchButton>
            <SketchButton
              onClick={handleAppleSignIn}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 text-base"
            >
              <SiApple className="w-6 h-6" style={{ color: 'var(--brand-from)' }} />
              {t('auth.continueWithApple')}
            </SketchButton>
            {!isAnonymous && (
              <SketchButton
                onClick={handleAnonymous}
                variant="ghost"
                className="w-full flex items-center justify-center gap-3 px-4 py-3 text-base"
                data-testid="try-anonymous-button"
              >
                <FaUserNinja className="w-6 h-6" style={{ color: 'var(--brand-from)' }} aria-hidden />
                {t('auth.tryAnonymously', 'Try anonymously')}
              </SketchButton>
            )}
          </div>

          <SketchButton
            variant="ghost"
            onClick={() => navigate('/')}
            className="mt-4 text-sm text-[var(--color-muted)]"
          >
            {t('buttons.cancel')}
          </SketchButton>
        </div>
      </SketchBox>
    </SketchPageShell>
  );
};

export default LoginPage;
